import { defineStore } from 'pinia'
import { useAuthStore } from './useAuthStore'
import type { Baby } from '../types'
import {
  getCollection,
  setDocument,
  updateDocument,
  deleteDocument,
} from '../services/firestoreService'

export const useBabyStore = defineStore('baby', {
  state: () => ({
    babies: [] as Baby[],
    loaded: false,
  }),

  actions: {
    async loadBabies() {
      const auth = useAuthStore()
      // 使用 dataOwnerUid：guest 讀 owner 的資料，owner 讀自己的資料
      if (!auth.dataOwnerUid) {
        return
      }

      const path = `users/${auth.dataOwnerUid}/babies`

      try {
        const babies = await getCollection<Omit<Baby, 'id'>>(path)

        this.babies = babies as Baby[]
        this.loaded = true
      } catch (error: any) {
        console.error('loadBabies 失敗:', {
          message: error?.message,
          code: error?.code,
          name: error?.name,
          type: typeof error
        })
        // 即使失敗也標記為已載入（空數組）
        this.loaded = true
        this.babies = []
      }
    },

    async setBaby(baby: Baby) {
      const auth = useAuthStore()

      console.log('[useBabyStore] setBaby 開始:', baby.id)
      console.log('[useBabyStore] auth.dataOwnerUid:', auth.dataOwnerUid)

      if (!auth.dataOwnerUid) {
        console.error('[useBabyStore] 沒有 dataOwnerUid，無法保存寶寶資料')
        throw new Error('用戶未登入')
      }

      const docPath = `users/${auth.dataOwnerUid}/babies/${baby.id}`
      console.log('[useBabyStore] Firestore 路徑:', docPath)

      try {
        console.log('[useBabyStore] 呼叫 setDocument...')
        await setDocument(docPath, {
          nickname: baby.nickname,
          birthDate: baby.birthDate,
          gender: baby.gender,
          createdAt: baby.createdAt,
          updatedBy: auth.uid, // 記錄寫入者（Cloud Functions 推播排除用）
        })
        console.log('[useBabyStore] setDocument 成功')
      } catch (error: any) {
        console.error('[useBabyStore] Firestore 保存失敗:', error)
        console.error('[useBabyStore] 錯誤詳情:', {
          message: error?.message,
          code: error?.code,
          name: error?.name,
          stack: error?.stack
        })
        throw error
      }
    },

    async updateBaby(babyId: string, data: Partial<Baby>) {
      const auth = useAuthStore()
      if (!auth.dataOwnerUid) return

      await updateDocument(`users/${auth.dataOwnerUid}/babies/${babyId}`, {
        ...data,
        updatedBy: auth.uid,
      })

      const baby = this.babies.find(b => b.id === babyId)
      if (baby) Object.assign(baby, data)
    },

    async deleteBaby(babyId: string) {
      const auth = useAuthStore()
      if (!auth.dataOwnerUid) return

      await deleteDocument(`users/${auth.dataOwnerUid}/babies/${babyId}`)

      this.babies = this.babies.filter(b => b.id !== babyId)
    },

    async deleteAllBabies() {
      const auth = useAuthStore()
      // 帳號刪除時使用 auth.uid（自己的資料），避免 guest 誤刪 owner 資料
      if (!auth.uid) return

      const path = `users/${auth.uid}/babies`
      const babies = await getCollection(path)

      for (const baby of babies) {
        await deleteDocument(`users/${auth.uid}/babies/${baby.id}`)
      }

      this.babies = []
      this.loaded = false
    },
  },
})
